import api from './api';
import type {
  StockByOwnerEntry,
  StockByConsignorEntry,
  MovementsByOwnerResponse,
  ConsignmentSummaryResponse,
  ConsignmentReconciliationResponse,
  ConsignmentStatement,
  CreateStatementPayload,
} from '../types/venue';

const base = (venueId: string) => `/admin/venue/${venueId}/ownership-reports`;

const ownershipReportService = {
  getStockByOwner: async (
    venueId: string,
    params?: { ownershipAccountId?: string; locationId?: string },
  ): Promise<StockByOwnerEntry[]> => {
    const response = await api.get(`${base(venueId)}/stock-by-owner`, { params });
    // API returns { ownerGroups: [{ ownershipAccount, totalQuantity, items }] }
    return (response.data.ownerGroups ?? []).map((g: any): StockByOwnerEntry => ({
      ownershipAccountId: g.ownershipAccount?._id ?? null,
      accountName: g.ownershipAccount?.name ?? '—',
      accountType: g.ownershipAccount?.type ?? 'VENUE_OWNED',
      totalQuantity: g.totalQuantity,
      items: (g.items ?? []).map((item: any) => ({
        inventoryItemId: item.inventoryItem?._id ?? item.inventoryItem,
        displayName: item.inventoryItem?.displayName ?? String(item.inventoryItem?._id ?? ''),
        quantity: item.totalQuantity,
      })),
    }));
  },

  getStockByConsignor: async (
    venueId: string,
    params?: { supplierId?: string; locationId?: string },
  ): Promise<StockByConsignorEntry[]> => {
    const response = await api.get(`${base(venueId)}/stock-by-consignor`, { params });
    // API returns { consignorGroups: [{ supplier, ownershipAccounts, totalQuantity }] }
    return (response.data.consignorGroups ?? []).map((g: any): StockByConsignorEntry => ({
      supplierId: g.supplier?._id ?? '_no_supplier',
      supplierName: g.supplier?.name ?? '(No Supplier)',
      totalQuantity: g.totalQuantity,
      accounts: (g.ownershipAccounts ?? []).map((a: any) => ({
        ownershipAccountId: a.ownershipAccount?._id ?? a.ownershipAccount,
        accountName: a.ownershipAccount?.name ?? '—',
        quantity: a.totalQuantity,
      })),
    }));
  },

  getMovementsByOwner: async (
    venueId: string,
    params?: {
      ownershipAccountId?: string;
      from?: string;
      to?: string;
      limit?: number;
    },
  ): Promise<MovementsByOwnerResponse> => {
    const response = await api.get(`${base(venueId)}/movements-by-owner`, { params });
    return response.data;
  },

  getConsignmentSummary: async (
    venueId: string,
    params?: { supplierId?: string; includeMovementSummary?: boolean },
  ): Promise<ConsignmentSummaryResponse> => {
    const response = await api.get(`${base(venueId)}/consignment-summary`, { params });
    // API returns { dataLimitation, consignors: [{ supplier, ownershipAccount, stockOnHand, lotsHeld, movementSummary }] }
    const accounts = (response.data.consignors ?? []).map((c: any) => {
      const byType: Record<string, number> = c.movementSummary?.byType ?? {};
      const totalIn  = Object.values(byType).filter((v: any) => v > 0).reduce((s: number, v: any) => s + v, 0);
      const totalOut = Object.values(byType).filter((v: any) => v < 0).reduce((s: number, v: any) => s + Math.abs(v), 0);
      return {
        ownershipAccountId: c.ownershipAccount?._id ?? c.ownershipAccount,
        accountName: c.ownershipAccount?.name ?? '—',
        supplierId: c.supplier?._id ?? null,
        supplierName: c.supplier?.name ?? null,
        lotsHeld: Array.isArray(c.lotsHeld) ? c.lotsHeld.length : (c.lotsHeld ?? 0),
        totalQuantity: c.stockOnHand?.totalQuantity ?? 0,
        movementSummary: c.movementSummary ? { totalIn, totalOut } : undefined,
      };
    });
    return { accounts, dataLimitation: response.data.dataLimitation };
  },

  getConsignmentReconciliation: async (
    venueId: string,
    params: {
      ownershipAccountId: string;
      from?: string;
      to?: string;
      includeMovements?: boolean;
    },
  ): Promise<ConsignmentReconciliationResponse> => {
    const response = await api.get(`${base(venueId)}/consignment-reconciliation`, { params });
    return response.data;
  },

  getStatements: async (
    venueId: string,
    params?: { ownershipAccountId?: string; limit?: number },
  ): Promise<ConsignmentStatement[]> => {
    const response = await api.get(`${base(venueId)}/statements`, { params });
    return response.data.statements ?? [];
  },

  createStatement: async (
    venueId: string,
    payload: CreateStatementPayload,
  ): Promise<ConsignmentStatement> => {
    const response = await api.post(`${base(venueId)}/statements`, payload);
    return response.data.statement;
  },
};

export default ownershipReportService;
